import { forwardRef, FunctionComponent, ReactNode } from 'react';
import { Box, BoxProps, useTheme, CircularProgress } from '@mui/material';
import { type Theme } from '@starjack/layout';

export type FlexContainerProps = {
    children?: ReactNode;
    fullWidth?: boolean;
    column?: boolean;
    center?: boolean;
} & BoxProps;

export const FlexContainer = forwardRef<unknown, FlexContainerProps>(
    (
        { center, column, fullWidth, children, flexDirection, ...boxProps },
        ref
    ) => (
        <Box
            ref={ref}
            {...boxProps}
            display='flex'
            flexDirection={column ? 'column' : flexDirection}
            width={fullWidth ? '100%' : boxProps.width}
            alignItems={center ? 'center' : boxProps.alignItems}
            justifyContent={center ? 'center' : boxProps.justifyContent}
        >
            {children}
        </Box>
    )
);

FlexContainer.displayName = 'FlexContainer';

export const RowContainer: FunctionComponent<
    React.PropsWithChildren<Exclude<FlexContainerProps, 'marginX'>>
> = ({ children, ...rest }) => {
    const theme: Theme = useTheme();

    return (
        <FlexContainer marginY={theme.spacing(1)} {...rest}>
            {children}
        </FlexContainer>
    );
};

export const SectionContainer: FunctionComponent<
    React.PropsWithChildren<Exclude<FlexContainerProps, 'marginX'>>
> = ({ children, ...rest }) => {
    const theme: Theme = useTheme();

    return (
        <FlexContainer
            marginX={theme.spacing(2)}
            marginY={theme.spacing(2)}
            {...rest}
        >
            {children}
        </FlexContainer>
    );
};

export const LoadingContainer: FunctionComponent<
    React.PropsWithChildren<FlexContainerProps & { cover?: boolean }>
> = ({ cover, sx, ...props }) => {
    const theme = useTheme();

    let sxProp;
    if (cover) {
        const coverProps = {
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
            position: 'absolute',
            left: 0,
            top: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
        };
        if (typeof sx === 'function') {
            sxProp = {
                ...sx(theme),
                ...coverProps,
            };
        } else {
            sxProp = {
                ...sx,
                ...coverProps,
            };
        }
    }

    return (
        <FlexContainer
            flexDirection='column'
            alignItems='center'
            justifyContent='center'
            flexGrow={1}
            {...props}
            sx={{
                ...sxProp,
            }}
        >
            <CircularProgress />
        </FlexContainer>
    );
};
